import axios from "axios";
import dayjs from "dayjs";
import has from "lodash/has";
import { takeLatest, call, put, select, all } from "redux-saga/effects";
import { createAction } from "redux-actions";
import FUNNEL_CONSTANTS from "../../constants";
import formatIntroFunnelChartData from "../../functions/formatIntroFunnelChartData";
import combineStats from "../../functions/combineStats";
import { stringify } from "../../../../../utils/stringify";

export const SET_LOADING = "saga/funnelChart/SET_LOADING";
export const SET_FILTERS_DIRTY = "saga/funnelChart/SET_FILTERS_DIRTY";
export const RESET_FILTERS = "saga/funnelChart/RESET_FILTERS";
export const SET_TABLES_LOADING = "saga/funnelChart/SET_TABLES_LOADING";
export const SET_REFERRAL_TABLE_LOADING = "saga/funnelChart/SET_REFERRAL_TABLE_LOADING";
export const SET_DATES = "saga/funnelChart/SET_DATES";
export const UPDATE_DATES = "saga/funnelChart/UPDATE_DATES";
export const UPDATE_TABLES = "saga/funnelChart/UPDATE_TABLES";
export const RESET_DATA = "saga/funnelChart/RESET_DATA";

export const FETCH_CURRENT_USER = "saga/funnelChart/FETCH_CURRENT_USER";
export const FETCH_CURRENT_USER_SUCCESS = "saga/funnelChart/FETCH_CURRENT_USER_SUCCESS";

export const FETCH_FUNNEL_CHART_SUCCESS = "saga/funnelChart/FETCH_FUNNEL_CHART_SUCCESS";
export const FETCH_FUNNEL_INTRO_OFFERS_CHART_SUCCESS =
  "saga/funnelChart/FETCH_FUNNEL_INTRO_OFFERS_CHART_SUCCESS";
export const FETCH_FUNNEL_CHART_FAILURE = "saga/funnelChart/FETCH_FUNNEL_CHART_FAILURE";
export const FETCH_REFERRAL_TABLE_SUCCESS = "saga/funnelChart/FETCH_REFERRAL_TABLE_SUCCESS";
export const FETCH_REFERRAL_TABLE_FAILURE = "saga/funnelChart/FETCH_REFERRAL_TABLE_FAILURE";
export const RESET_REFERRAL_TABLE = "saga/funnelChart/RESET_REFERRAL_TABLE";
export const UPDATE_CUSTOM_RANGE = "saga/funnelChart/UPDATE_CUSTOM_RANGE";
export const UPDATE_CHART = "saga/funnelChart/UPDATE_CHART";
export const UPDATE_START_FROM = "saga/funnelChart/UPDATE_START_FROM";
export const TOGGLE_LOOSE = "saga/funnelChart/TOGGLE_LOOSE";
export const TOGGLE_FUTURE_APPOINTMENT = "saga/funnelChart/TOGGLE_FUTURE_APPOINTMENT";
export const TOGGLE_EXCLUDE_ENROLLED = "saga/funnelChart/TOGGLE_EXCLUDE_ENROLLED";

export const FETCH_FILTERS_SUCCESS = "saga/funnelChart/FETCH_FILTERS_SUCCESS";
export const SET_SUB_AVERAGE_FILTER_SELECTED_OPTIONS =
  "saga/funnelChart/SET_SUB_AVERAGE_FILTER_SELECTED_OPTIONS";
export const SET_PROSPECT_TYPES = "saga/funnelChart/SET_PROSPECT_TYPES";

export const SET_AVERAGE = "saga/funnelChart/SET_AVERAGE";
export const SET_AVERAGE_TOOLTIP = "saga/funnelChart/SET_AVERAGE_TOOLTIP";
export const SET_START_FROM = "saga/funnelChart/SET_START_FROM";
export const SET_START_FROM_APPOINTMENT = "saga/funnelChart/SET_START_FROM_APPOINTMENT";
export const SET_TOGGLE_LOOSE = "saga/funnelChart/SET_TOGGLE_LOOSE";
export const SET_TOGGLE_FUTURE_APPOINTMENT = "saga/funnelChart/SET_TOGGLE_FUTURE_APPOINTMENT";
export const SET_TOGGLE_EXCLUDE_ENROLLED = "saga/funnelChart/SET_TOGGLE_EXCLUDE_ENROLLED";
export const SET_TOGGLE_REFERRAL_CATEGORY = "saga/funnelChart/SET_TOGGLE_REFERRAL_CATEGORY";
export const ADD_ALL_TIME_RANGE_SUCCESS = "saga/funnelChart/ADD_ALL_TIME_RANGE_SUCCESS";

export const actionCreators = {
  fetchData: createAction(FETCH_CURRENT_USER),
  updateDates: createAction(UPDATE_DATES),
  updateChart: createAction(UPDATE_CHART),
  updateStartFrom: createAction(UPDATE_START_FROM),
  setAverage: createAction(SET_AVERAGE),
  setProspectTypes: createAction(SET_PROSPECT_TYPES),
  resetData: createAction(RESET_DATA),
  toggleLoose: createAction(TOGGLE_LOOSE),
  toggleFutureAppointment: createAction(TOGGLE_FUTURE_APPOINTMENT),
  toggleExcludeEnrolled: createAction(TOGGLE_EXCLUDE_ENROLLED),
  toggleReferralCategory: createAction(SET_TOGGLE_REFERRAL_CATEGORY),
  requestNewTableData: createAction(UPDATE_TABLES),
  updateCustomRange: createAction(UPDATE_CUSTOM_RANGE),
  setSubAverageSelectedOptions: createAction(SET_SUB_AVERAGE_FILTER_SELECTED_OPTIONS)
};

export default function* watchSummary() {
  yield takeLatest(FETCH_CURRENT_USER, fetchData);
  yield takeLatest(UPDATE_CHART, updateChart);
  yield takeLatest(UPDATE_START_FROM, updateStartFrom);
  yield takeLatest(TOGGLE_FUTURE_APPOINTMENT, toggleFutureAppointment);
  yield takeLatest(TOGGLE_EXCLUDE_ENROLLED, toggleExcludeEnrolled);
  yield takeLatest(SET_TOGGLE_REFERRAL_CATEGORY, toggleReferralCategory);
  yield takeLatest(TOGGLE_LOOSE, toggleLoose);
  yield takeLatest(RESET_DATA, resetData);
}

function* getAverageApiParams() {
  const {
    currentUser: { showLaserNetworkAverages },
    selectedFilters: { subAverageFilterOptions, average }
  } = yield select((state) => state.laser);

  return showLaserNetworkAverages
    ? {
      average_type: average,
      averages_object_ids: stringify(subAverageFilterOptions) || undefined
    }
    : {};
}

function* getCommonApiParams() {
  const { organization_id, short_url } = yield select((state) => state.commonReducer.commonProps);

  const {
    currentUser: { showFutureAppointment },
    selectedFilters: {
      startFrom,
      startDate,
      endDate,
      prospectTypes,
      toggleLoose,
      toggleFutureAppointment,
      toggleExcludeEnrolled
    }
  } = yield select((state) => state.laser);

  const leadParams =
    startFrom.id == "leads"
      ? {
        show_future_appointment: showFutureAppointment ? toggleFutureAppointment : undefined,
        exclude_enrolled_bookings: toggleExcludeEnrolled,
        prospect_type_ids: stringify(prospectTypes)
      }
      : {};

  const toggleLooseParam =
    startFrom.id == "shows" || startFrom.id == "enrollments"
      ? {}
      : { purchases_in_conversion: toggleLoose };

  return {
    organization_id,
    short_url,
    start_date: startDate,
    end_date: endDate,
    start_from: startFrom.id,
    ...leadParams,
    ...toggleLooseParam
  };
}

function* fetchFilters() {
  const { organization_id, short_url } = yield select((state) => state.commonReducer.commonProps);

  try {
    const res = yield call(axios.get, FUNNEL_CONSTANTS.REQUEST_HREF.FUNNEL_LASER_CHART_FILTERS, {
      params: { organization_id, short_url }
    });

    yield put({
      type: FETCH_FILTERS_SUCCESS,
      payload: res.data
    });
  } catch (error) {
    console.error(error);
  }
}

function* setDates(calendarEndDate) {
  let startDate = dayjs(calendarEndDate).startOf("month").format("YYYY-MM-DD");
  let endDate = calendarEndDate;

  yield put({ type: SET_DATES, payload: { startDate: startDate, endDate: endDate } });
}

function* fetchData({ payload }) {
  yield put({ type: FETCH_CURRENT_USER_SUCCESS, payload: payload });

  //sub average filters, prospect_types
  yield fetchFilters();

  const {
    startFromAppointment,
    showFutureAppointment,
    showFutureAppointmentByDefault,
    calendarEndDate
  } = payload;

  if (startFromAppointment) {
    yield put({
      type: SET_START_FROM,
      payload: FUNNEL_CONSTANTS.START_FROM_OPTIONS[1]
    });
  }

  if (showFutureAppointment && showFutureAppointmentByDefault) {
    yield put({
      type: SET_TOGGLE_FUTURE_APPOINTMENT,
      payload: showFutureAppointmentByDefault
    });
  }

  yield setDates(calendarEndDate);

  try {
    yield requestByStartFrom();
    yield put({ type: SET_LOADING, payload: false });
  } catch (error) {
    console.error(error);
  }
}

function* requestByStartFrom() {
  const { start_from, ...rest } = yield getCommonApiParams();
  const averageParams = yield getAverageApiParams();

  if (start_from === "leads") {
    yield all([
      fetchFunnelChartData({ start_from, ...rest, ...averageParams }),
      fetchReferralTableData(rest)
    ]);
  } else if (start_from === "introOffer") {
    yield fetchFunnelIntroOfferChartData({ ...rest, ...averageParams });
  } else {
    yield fetchFunnelChartData({
      start_from,
      ...rest,
      ...averageParams
    });
  }
}

function* updateStartFrom(payload) {
  yield put({ type: SET_START_FROM, payload: payload.payload });
  yield resetData();
}

function* toggleFutureAppointment(payload) {
  yield put({ type: SET_TOGGLE_FUTURE_APPOINTMENT, payload: payload.payload });
  yield put({ type: SET_TABLES_LOADING, payload: true });

  try {
    yield requestByStartFrom();

    yield put({ type: SET_TABLES_LOADING, payload: false });
  } catch (error) {
    console.error(error);
  }
}

function* toggleExcludeEnrolled({ payload }) {
  yield put({ type: SET_TOGGLE_EXCLUDE_ENROLLED, payload });
  yield put({ type: SET_TABLES_LOADING, payload: true });

  try {
    yield requestByStartFrom();

    yield put({ type: SET_TABLES_LOADING, payload: false });
  } catch (error) {
    console.error(error);
  }
}

function* toggleReferralCategory() {
  yield put({ type: SET_REFERRAL_TABLE_LOADING, payload: true });
  yield put({ type: SET_FILTERS_DIRTY, payload: false });

  const { start_from, ...rest } = yield getCommonApiParams();

  try {
    yield fetchReferralTableData(rest);
    yield put({ type: SET_REFERRAL_TABLE_LOADING, payload: false });
  } catch (error) {
    console.error(error);
  }
}

function* toggleLoose(payload) {
  yield put({ type: SET_TOGGLE_LOOSE, payload: payload.payload });
  yield put({ type: SET_TABLES_LOADING, payload: true });
  yield put({ type: SET_FILTERS_DIRTY, payload: false });

  try {
    yield requestByStartFrom();

    yield put({ type: SET_TABLES_LOADING, payload: false });
  } catch (error) {
    console.log("updateChart", error);
  }
}

function* updateChart() {
  yield put({ type: SET_TABLES_LOADING, payload: true });
  yield put({ type: SET_FILTERS_DIRTY, payload: false });
  const average = yield select((state) => state.laser.selectedFilters.average);

  if (average && average !== "Not Selected") {
    yield put({ type: SET_AVERAGE_TOOLTIP, payload: average + " Averages" });
  }

  try {
    yield requestByStartFrom();
    yield put({ type: SET_TABLES_LOADING, payload: false });
  } catch (error) {
    console.log("updateChart", error);
  }
}

function* fetchFunnelChartData(params) {
  try {
    const res = yield call(axios.post, FUNNEL_CONSTANTS.REQUEST_HREF.FUNNEL_LASER_CHART, params);

    if (!res) {
      return;
    }

    if (has(res, "data.first_data_date")) {
      yield put({ type: ADD_ALL_TIME_RANGE_SUCCESS, payload: res.data.first_data_date });
    }
    yield put({ type: FETCH_FUNNEL_CHART_SUCCESS, payload: res.data });
  } catch (error) {
    console.error(error);
    yield put({ type: FETCH_FUNNEL_CHART_FAILURE, error });
  }
}

function* fetchFunnelIntroOfferChartData(params) {
  try {
    const res = yield call(axios.get, FUNNEL_CONSTANTS.REQUEST_HREF.FUNNEL_INTRO_OFFER, {
      params
    });

    if (!res.data) {
      yield put({ type: FETCH_FUNNEL_INTRO_OFFERS_CHART_SUCCESS, payload: null });
      return;
    }

    if (has(res, "data.first_data_date")) {
      yield put({ type: ADD_ALL_TIME_RANGE_SUCCESS, payload: res.data.first_data_date });
    }
    const result = {
      ...res.data,
      combinedStats: combineStats(res.data.primaryStats, res.data.secondaryStats) || [],
      chart: {
        label: res.data.chart.label,
        data: formatIntroFunnelChartData(res.data.chart)
      }
    };

    yield put({ type: FETCH_FUNNEL_INTRO_OFFERS_CHART_SUCCESS, payload: result });
  } catch (error) {
    console.error(error);
    yield put({ type: FETCH_FUNNEL_CHART_FAILURE, error });
  }
}

function* fetchReferralTableData(params) {
  const { toggleReferralCategory } = yield select((state) => state.laser.selectedFilters);
  try {
    const res = yield call(axios.post, FUNNEL_CONSTANTS.REQUEST_HREF.FUNNEL_TABLE, {
      ...params,
      referral_category: toggleReferralCategory
    });

    if (has(res, "data.first_data_date")) {
      yield put({ type: ADD_ALL_TIME_RANGE_SUCCESS, payload: res.data.first_data_date });
    }
    yield put({ type: FETCH_REFERRAL_TABLE_SUCCESS, payload: res.data });
  } catch (error) {
    console.error(error);
    yield put({ type: FETCH_REFERRAL_TABLE_FAILURE, error });
  }
}

function* resetData() {
  yield put({ type: SET_LOADING, payload: true });
  yield put({ type: SET_FILTERS_DIRTY, payload: false });

  yield put({ type: RESET_FILTERS });

  const { calendarEndDate } = yield select((state) => state.laser.currentUser);

  yield setDates(calendarEndDate);

  try {
    yield requestByStartFrom();
    yield put({ type: SET_LOADING, payload: false });
  } catch (error) {
    console.error(error);
  }
}
